import React, { useState } from 'react';
import { Plus, Trash2, Edit2, Save, X, Users, Shield } from 'lucide-react';

export default function RoleCreationStep({ wizardData, updateWizardData, setError }) {
  const [editingRole, setEditingRole] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    hierarchyLevel: 50
  });
  const [showForm, setShowForm] = useState(false);

  // Pre-configured role templates
  const roleTemplates = [
    {
      name: 'sales_manager',
      displayName: 'Sales Manager',
      description: 'Manages sales team and oversees deals pipeline',
      hierarchyLevel: 60,
      icon: '👔'
    },
    {
      name: 'sales_rep',
      displayName: 'Sales Representative',
      description: 'Handles customer interactions and closes deals',
      hierarchyLevel: 40,
      icon: '💼'
    },
    {
      name: 'customer_service',
      displayName: 'Customer Service',
      description: 'Provides support and manages customer inquiries',
      hierarchyLevel: 40,
      icon: '🎧'
    },
    {
      name: 'account_manager',
      displayName: 'Account Manager',
      description: 'Maintains relationships with key accounts',
      hierarchyLevel: 55,
      icon: '🤝'
    }
  ];

  const handleTemplateSelect = (template) => {
    const newRole = {
      tempId: `temp_${Date.now()}`,
      name: template?.name,
      displayName: template?.displayName,
      description: template?.description,
      hierarchyLevel: template?.hierarchyLevel,
      isTemplate: true
    };

    updateWizardData('roles', [...wizardData?.roles, newRole]);
  };

  const handleAddCustomRole = () => {
    if (!formData?.name || !formData?.displayName) {
      setError?.('Please provide both role name and display name');
      return;
    }

    const newRole = {
      tempId: editingRole ? editingRole?.tempId : `temp_${Date.now()}`,
      name: formData?.name,
      displayName: formData?.displayName,
      description: formData?.description,
      hierarchyLevel: parseInt(formData?.hierarchyLevel),
      isTemplate: false
    };

    if (editingRole) {
      // Update existing role
      const updatedRoles = wizardData?.roles?.map(r => 
        r?.tempId === editingRole?.tempId ? newRole : r
      );
      updateWizardData('roles', updatedRoles);
    } else {
      // Add new role
      updateWizardData('roles', [...wizardData?.roles, newRole]);
    }

    // Reset form
    setFormData({
      name: '',
      displayName: '',
      description: '',
      hierarchyLevel: 50
    });
    setShowForm(false);
    setEditingRole(null);
    setError?.('');
  };

  const handleEditRole = (role) => {
    setEditingRole(role);
    setFormData({
      name: role?.name,
      displayName: role?.displayName,
      description: role?.description || '',
      hierarchyLevel: role?.hierarchyLevel
    });
    setShowForm(true);
  };

  const handleDeleteRole = (roleId) => {
    const updatedRoles = wizardData?.roles?.filter(r => r?.tempId !== roleId);
    updateWizardData('roles', updatedRoles);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingRole(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      hierarchyLevel: 50
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Create Business Roles</h2>
        <p className="mt-2 text-gray-600">
          Define the roles that your team members will have. You can start with pre-configured templates or create custom roles.
        </p>
      </div>
      {/* Role Templates Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Start: Use Role Templates</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roleTemplates?.map((template, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => handleTemplateSelect(template)}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="text-3xl">{template?.icon}</div>
                  <div>
                    <h4 className="font-semibold text-gray-900">{template?.displayName}</h4>
                    <p className="text-sm text-gray-600 mt-1">{template?.description}</p>
                    <div className="mt-2 flex items-center text-xs text-gray-500">
                      <Shield className="w-3 h-3 mr-1" />
                      <span>Hierarchy Level: {template?.hierarchyLevel}</span>
                    </div>
                  </div>
                </div>
                <button className="text-blue-600 hover:text-blue-800">
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Custom Role Form */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Or Create Custom Role</h3>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Custom Role
            </button>
          )}
        </div>

        {showForm && (
          <div className="border border-gray-200 rounded-lg p-6 bg-gray-50">
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role Name (System) *
                  </label>
                  <input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => setFormData({ ...formData, name: e?.target?.value })}
                    placeholder="e.g., sales_manager"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Lowercase, no spaces (use underscores)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name *
                  </label>
                  <input
                    type="text"
                    value={formData?.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e?.target?.value })}
                    placeholder="e.g., Sales Manager"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Friendly name shown in UI
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData?.description}
                  onChange={(e) => setFormData({ ...formData, description: e?.target?.value })}
                  placeholder="Describe the role's responsibilities..."
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hierarchy Level (1-100)
                </label>
                <div className="flex items-center space-x-4">
                  <input
                    type="range"
                    min="1"
                    max="100"
                    value={formData?.hierarchyLevel}
                    onChange={(e) => setFormData({ ...formData, hierarchyLevel: e?.target?.value })}
                    className="flex-1"
                  />
                  <span className="text-sm font-medium text-gray-700 w-12 text-center">
                    {formData?.hierarchyLevel}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Higher numbers = higher authority (Super Admin: 100, Staff: 40)
                </p>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <button
                  onClick={handleCancelForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  <X className="w-4 h-4 inline mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleAddCustomRole}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  <Save className="w-4 h-4 inline mr-2" />
                  {editingRole ? 'Update Role' : 'Add Role'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Created Roles List */}
      {wizardData?.roles?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Created Roles ({wizardData?.roles?.length})
          </h3>
          <div className="space-y-3">
            {wizardData?.roles?.map((role) => (
              <div
                key={role?.tempId}
                className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <Users className="w-5 h-5 text-gray-400" />
                      <div>
                        <h4 className="font-semibold text-gray-900">{role?.displayName}</h4>
                        <p className="text-sm text-gray-600 mt-1">{role?.description || 'No description'}</p>
                        <div className="flex items-center mt-2 space-x-4 text-xs text-gray-500">
                          <span>System Name: {role?.name}</span>
                          <span>•</span>
                          <span className="flex items-center">
                            <Shield className="w-3 h-3 mr-1" />
                            Level {role?.hierarchyLevel}
                          </span>
                          {role?.isTemplate && (
                            <>
                              <span>•</span>
                              <span className="text-blue-600 font-medium">Template</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleEditRole(role)}
                      className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteRole(role?.tempId)}
                      className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Empty State */}
      {wizardData?.roles?.length === 0 && (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No roles created yet</h3>
          <p className="text-gray-600 mb-4">
            Start by selecting a template or creating a custom role
          </p>
        </div>
      )}
      {/* Help Section */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">💡 Tips for Creating Roles</h4>
        <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
          <li>Start with common business roles like Manager, Sales Rep, Customer Service</li>
          <li>Use hierarchy levels to establish clear reporting structure</li>
          <li>Keep role names descriptive and easy to understand</li>
          <li>You can always modify roles later in Settings</li>
        </ul>
      </div>
    </div>
  );
}